import { trackPromise } from 'react-promise-tracker'
// import { GET_USER_FILTERS } from '../../types'
import { parseResponse } from '@jweb-development/response-parser'
import { GET_FILTER_DETAILS, GET_USER_FILTERS, DATA_REQUEST, DATA_SUCCESS, DATA_ERROR } from '../../types'

const api = (process.env.REACT_APP_ON_PREM === true || process.env.REACT_APP_ON_PREM === 'true') && window.location.protocol === 'http:'
  ? 'http://' + window.location.hostname + ':3080'
  : (process.env.REACT_APP_ON_PREM === true || process.env.REACT_APP_ON_PREM === 'true') && window.location.protocol === 'https:'
      ? 'https://' + window.location.hostname + ':8443'
      : process.env.REACT_APP_API_URL

/**
* Callback called on request success
* @callback requestSuccess
*/

/**
* Callback called on request failure
* @callback requestFailure
*/

/**
 * Function to get user's filters
 * @param {requestSuccess} fireSuccess function triggered on success
 * @param {requestFailure} fireFailure function triggered on failure
 */
export const getUserFilters = (fireSuccess = () => { }, fireFailure = () => { }) => {
  return async (dispatch, getState) => {
    try {
      const { token, accountId: accountID, supervisorID } = getState().auth

      dispatch({ type: DATA_REQUEST, payload: { type: GET_USER_FILTERS } })

      const options = {
        method: 'GET',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      }

      const response = await window.fetch(`${api}/accounts/${accountID}/supervisors/${supervisorID}/filters`, options)
      const parsedResponse = parseResponse(response)

      if (parsedResponse && !parsedResponse.error) {
        const { data: supervisorFilters = [] } = await response.json()

        dispatch({ type: DATA_SUCCESS, payload: { type: GET_USER_FILTERS } })

        dispatch({ type: GET_USER_FILTERS, payload: supervisorFilters })
      } else {
        fireFailure()

        dispatch({ type: DATA_ERROR, payload: { type: GET_USER_FILTERS } })
      }
    } catch (err) {
      console.error(err)
      fireFailure()
    }
  }
}

/**
 * Get specific filter details
 * @param {String} filterID id of filter to pull
 * @param {requestSuccess} fireSuccess function triggered on success
 * @param {requestFailure} fireFailure function triggered on failure
 */
export const getFilterDetails = (filterID, fireSuccess = () => { }, fireFailure = () => { }) => {
  return async (dispatch, getState) => {
    try {
      const { token, accountId: accountID, supervisorID } = getState().auth

      const options = {
        method: 'GET',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      }

      const response = await trackPromise(
        window.fetch(`${api}/accounts/${accountID}/supervisors/${supervisorID}/filters/${filterID}`, options)
      )
      const parsedResponse = parseResponse(response)

      if (parsedResponse && !parsedResponse.error) {
        const { data: [filterDetails] = {} } = await response.json()
        const {
          filterID = '', filterName = '', startTime = 0, endTime = 0, startDate = 0, endDate = 0,
          capture_source: captureSource = null, severityMin = 0, severityMax = 100,
          workstations = [], users = [], categories = [], statuses = [], isFastFilter = 0
        } = filterDetails

        const payload = {
          filterID,
          filterName,
          startTime,
          endTime,
          startDate,
          endDate,
          captureSource,
          severityMin,
          severityMax,
          workstations,
          users,
          categories,
          statuses,
          isFastFilter
        }

        dispatch({ type: GET_FILTER_DETAILS, payload })
      } else {
        fireFailure()
      }
    } catch (err) {
      console.error(err)
      fireFailure()
    }
  }
}

/**
 * Creates new filter
 * @param {Object} newFilter info for new filter
 * @param {String} newFilter.filterName new filter name
 * @param {Number} newFilter.startDate new filter unix start date
 * @param {Number} newFilter.endDate new filter unix end date
 * @param {Number} newFilter.severityMin new filter minimum severity
 * @param {Number} newFilter.severityMax new filter maximum severity
 * @param {Number} [newFilter.startTime] new filter start time
 * @param {Number} [newFilter.endTime] new filter end time
 * @param {Number} [newFilter.source] new filter source of capture number type [keyboard | ocr | all]
 * @param {Array.<String>} [newFilter.workstationsToAdd] array of id's of new filter workstations
 * @param {Array.<String>} [newFilter.usersToAdd] array of id's of new filter users
 * @param {Array.<String>} [newFilter.categoriesToAdd] array of id's of new filter categories
 * @param {Array.<String>} [newFilter.statusesToAdd] array of id's of new filter statuses
 * @param {Boolean} [newFilter.isFastFilter] new filter boolean value for fast filter option
 * @param {requestSuccess} fireSuccess function triggered on success
 * @param {requestFailure} fireFailure function triggered on failure
 */
export const createFilter = (newFilter, fireSuccess = () => { }, fireFailure = () => { }) => {
  return async (dispatch, getState) => {
    try {
      const { token, accountId: accountID, supervisorID } = getState().auth

      const body = JSON.stringify(newFilter)

      const options = {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body
      }

      const response = await trackPromise(
        window.fetch(`${api}/accounts/${accountID}/supervisors/${supervisorID}/filters`, options)
      )

      const parsedResponse = parseResponse(response)

      if (parsedResponse && !parsedResponse.error) {
        const { data } = await response.json()

        fireSuccess(data.filterID)
      } else {
        fireFailure()
      }
    } catch (err) {
      console.error(err)
      fireFailure()
    }
  }
}

/**
 * Updates current filter
 * @param {String} filterID id of filter to update
 * @param {Object} filterInfo info for updating filter
 * @param {String} filterInfo.filterName current filter new filter name
 * @param {Number} filterInfo.startDate current filter new unix start date
 * @param {Number} filterInfo.endDate current filter new unix end date
 * @param {Number} filterInfo.severityMin current filter new minimum severity
 * @param {Number} filterInfo.severityMax current filter new maximum severity
 * @param {Number} [filterInfo.startTime] current filter new start time
 * @param {Number} [filterInfo.endTime] current filter new end time
 * @param {Number} [filterInfo.source] current filter new source of capture [keyboard | ocr | all]
 * @param {Array.<String>} [newFilter.workstationsToAdd] current filter array with id's of new workstations
 * @param {Array.<String>} [newFilter.workstationsToDelete] current filter array with id's of workstations to delete
 * @param {Array.<String>} [newFilter.usersToAdd] current filter array with id's of users to add
 * @param {Array.<String>} [newFilter.usersToDelete] current filter array with id's of users to delete
 * @param {Array.<String>} [newFilter.categoriesToAdd] current filter array with id's of categories to add
 * @param {Array.<String>} [newFilter.categoriesToDelete] current filter array with id's of categories to delete
 * @param {Array.<String>} [newFilter.statusesToAdd] current filter array with id's of statuses to add
 * @param {Array.<String>} [newFilter.statusesToAdd] current filter array with id's of statuses to delete
 * @param {Boolean} [newFilter.isFastFilter] new filter boolean value for fast filter option
 * @param {requestSuccess} fireSuccess function triggered on success
 * @param {requestFailure} fireFailure function triggered on failure
 */
export const updateFilter = (filterID, filterInfo, type, fireSuccess = () => { }, fireFailure = () => { }) => {
  return async (dispatch, getState) => {
    try {
      const { token, accountId: accountID, supervisorID } = getState().auth

      const body = JSON.stringify(filterInfo)

      const options = {
        method: 'PUT',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body
      }

      const response = await trackPromise(
        window.fetch(`${api}/accounts/${accountID}/supervisors/${supervisorID}/filters/${filterID}`, options)
      )
      const parsedResponse = parseResponse(response)

      if (parsedResponse && !parsedResponse.error) {
        fireSuccess(filterID, type)
      } else {
        fireFailure()
      }
    } catch (err) {
      console.error(err)
      fireFailure()
    }
  }
}

export const updateFastFilter = (filterID, isFastFilter, fireSuccess = () => { }, fireFailure = () => { }) => {
  return async (dispatch, getState) => {
    try {
      const { token, accountId: accountID, supervisorID } = getState().auth

      const body = JSON.stringify({ isFastFilter })

      const options = {
        method: 'PUT',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body
      }

      const response = await trackPromise(
        window.fetch(`${api}/accounts/${accountID}/supervisors/${supervisorID}/filters/${filterID}/fastFilter`, options),
        'updating-fast-filter'
      )
      const parsedResponse = parseResponse(response)

      if (parsedResponse && !parsedResponse.error) {
        fireSuccess(filterID)
      } else {
        fireFailure()
      }
    } catch (err) {
      console.error(err)
      fireFailure()
    }
  }
}

/**
 * Deletes specific filter
 * @param {String} filterID id of current filter
 * @param {requestSuccess} fireSuccess function triggered on success
 * @param {requestFailure} fireFailure function triggered on failure
 */
export const deleteFilter = (filterID, fireSuccess = () => { }, fireFailure = () => { }) => {
  return async (dispatch, getState) => {
    const { token, accountId: accountID, supervisorID } = getState().auth

    try {
      const options = {
        method: 'DELETE',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      }

      const response = await trackPromise(
        window.fetch(`${api}/accounts/${accountID}/supervisors/${supervisorID}/filters/${filterID}`, options)
      )
      const parsedResponse = parseResponse(response)

      if (parsedResponse && !parsedResponse.error) {
        fireSuccess()
      } else {
        fireFailure()
      }
    } catch (err) {
      console.error(err)
      fireFailure()
    }
  }
}
